import {Button} from 'antd';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';

import DBModal from 'components/modal/modal';
import {prettifyEnum} from 'utils/currencyFormatting';
import {formatAmount} from './amountField';
import {summariseMethodConfigs} from './paymentMethodConfig';
import styles from './createWalletCurrency.module.scss';

const onOff = value => (value ? 'On' : 'Off');

const isPlainObject = value =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const describe = (key, value, values) => {
  if (key.endsWith('MethodConfigs')) {
    // Summarised rather than listed: the raw object is per-method, per-field,
    // per-language, and dumping it here would bury the one line that changed.
    const methods =
      key === 'depositMethodConfigs'
        ? values?.depositMethods
        : values?.withdrawalMethods;
    return summariseMethodConfigs(methods, value);
  }
  if (key.endsWith('Enabled')) return onOff(value);
  if (key.endsWith('Methods')) {
    if (!value?.length) return 'None';
    return value.map(prettifyEnum).join(', ');
  }
  if (key.endsWith('Limit')) return formatAmount(value ?? '') || '—';
  return value ?? '—';
};

const ROWS = [
  {key: 'currency', label: 'Currency'},
  {key: 'currencyName', label: 'Name'},
  {key: 'currencySymbol', label: 'Symbol'},
  {key: 'currencyEnabled', label: 'Currency enabled'},
  {key: 'displayEnabled', label: 'Shown to users'},
  {key: 'depositEnabled', label: 'Deposits enabled'},
  {key: 'depositMethods', label: 'Deposit methods'},
  {key: 'depositDailyLimit', label: 'Deposit daily limit'},
  {key: 'depositWeeklyLimit', label: 'Deposit weekly limit'},
  {key: 'depositMonthlyLimit', label: 'Deposit monthly limit'},
  {key: 'depositMethodConfigs', label: 'Deposit method setup'},
  {key: 'withdrawalEnabled', label: 'Withdrawals enabled'},
  {key: 'withdrawalMethods', label: 'Withdrawal methods'},
  {key: 'withdrawalDailyLimit', label: 'Withdrawal daily limit'},
  {key: 'withdrawalWeeklyLimit', label: 'Withdrawal weekly limit'},
  {key: 'withdrawalMonthlyLimit', label: 'Withdrawal monthly limit'},
  {key: 'withdrawalMethodConfigs', label: 'Withdrawal method setup'},
];

const sameValue = (a, b) => {
  // The method configs are nested objects, so the string comparison below
  // would call every save a change. Compare what actually gets submitted.
  if (isPlainObject(a) || isPlainObject(b)) {
    return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = [...(a ?? [])].sort().join('|');
    const right = [...(b ?? [])].sort().join('|');
    return left === right;
  }
  if (a == null && b == null) return true;
  return String(a ?? '') === String(b ?? '');
};

/**
 * Last stop before writing. In update mode it lists only what actually
 * changes, with the old value struck through beside the new one — the form
 * used to submit silently, so a flag flipping (say withdrawals switching back
 * on) left no trace for the operator to catch.
 */
const CurrencyConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  values,
  original,
  isUpdate,
}) => {
  const changed = ROWS.filter(
    row => !isUpdate || !sameValue(original?.[row.key], values?.[row.key]),
  );
  const nothingChanged = isUpdate && changed.length === 0;

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={onClose}
      width={620}
      content={
        <div className={styles.confirmModal}>
          <div className={styles.confirmHeader}>
            <h1 className={styles.confirmTitle}>
              {isUpdate
                ? `Update ${values?.currency} settings?`
                : `Create ${values?.currency ?? 'this currency'}?`}
            </h1>
            <p className={styles.confirmSubtitle}>
              {nothingChanged
                ? 'Nothing on the form differs from the saved settings.'
                : isUpdate
                ? 'These are the only values that will change.'
                : 'Check the settings below before creating the currency.'}
            </p>
          </div>

          {!nothingChanged && (
            <div className={styles.confirmList}>
              {changed.map(row => (
                <div className={styles.confirmRow} key={row.key}>
                  <span className={styles.confirmLabel}>{row.label}</span>
                  <span className={styles.confirmValues}>
                    {isUpdate && (
                      <>
                        <span className={styles.confirmOld}>
                          {describe(row.key, original?.[row.key], original)}
                        </span>
                        <span className={styles.confirmArrow}>→</span>
                      </>
                    )}
                    <span className={styles.confirmNew}>
                      {describe(row.key, values?.[row.key], values)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {(values?.currencyEnabled === false ||
            values?.depositEnabled === false ||
            values?.withdrawalEnabled === false) && (
            <div className={styles.confirmWarning}>
              <ExclamationCircleOutlined />
              <span>
                Saving with{' '}
                {[
                  values?.currencyEnabled === false && 'the currency',
                  values?.depositEnabled === false && 'deposits',
                  values?.withdrawalEnabled === false && 'withdrawals',
                ]
                  .filter(Boolean)
                  .join(', ')}{' '}
                turned off stops users transacting straight away.
              </span>
            </div>
          )}

          <div className={styles.confirmFooter}>
            <Button onClick={onClose}>Back</Button>
            <Button
              type="primary"
              className={styles.primaryButton}
              loading={loading}
              disabled={nothingChanged}
              onClick={onConfirm}>
              {isUpdate ? 'Save changes' : 'Create currency'}
            </Button>
          </div>
        </div>
      }
    />
  );
};

CurrencyConfirmModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  loading: PropTypes.bool,
  values: PropTypes.object,
  original: PropTypes.object,
  isUpdate: PropTypes.bool,
};

export default CurrencyConfirmModal;
